
import { useParams, useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import { CardDetail } from "@/components/cards/CardDetail";
import { Button } from "@/components/ui/button";
import { CardGrid } from "@/components/cards/CardGrid";
import { ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { getCardById, PokemonCard } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const CardDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState<PokemonCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);
  
  // Fetch card data
  useEffect(() => {
    const fetchCard = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const cardData = await getCardById(id);
        setCard(cardData);
      } catch (err) {
        console.error("Failed to fetch card:", err);
        setError("Failed to load card details");
      } finally {
        setLoading(false);
      }
    };
    
    fetchCard();
  }, [id]);
  
  const handleGoBack = () => {
    window.history.back();
    // Add a slight delay before scrolling to ensure the previous page has loaded
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  if (!id) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-2">Card Not Found</h2>
          <p className="text-muted-foreground mb-6">
            We couldn't find the card you were looking for.
          </p>
          <Button onClick={handleGoBack}>Go Back</Button>
        </div>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={handleGoBack}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Skeleton className="aspect-[2/3] w-full" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-6 w-1/3" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-2">Error</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={handleGoBack}>Go Back</Button>
        </div>
      ) : (
        <CardDetail cardId={id} />
      )}
      
      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Similar Cards</h2>
        {card ? (
          <CardGrid pageSize={5} similarTo={card} />
        ) : (
          <div className="h-64 flex items-center justify-center">
            <Skeleton className="h-full w-full" />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default CardDetailPage;
